// Smart search bar with 300ms debounce and instant results dropdown

import { useState, useEffect, useRef, useCallback } from "react";
import { Search, X, Loader2 } from "lucide-react";
import { Input } from "@cieda/ui";
import { sanitizeDisplayText } from "@/lib/sanitize";

interface ProductResult {
  readonly id: string;
  readonly name: string;
  readonly brand: string;
  readonly category: string;
  readonly price_fcfa: number;
  readonly image_url: string | null;
  readonly score: number;
}

interface SearchBarProps {
  readonly onSelectProduct?: (productId: string) => void;
  readonly placeholder?: string;
  readonly className?: string;
}

const API_URL = import.meta.env.VITE_API_URL || "";

export function SearchBar({
  onSelectProduct,
  placeholder = "Rechercher un produit...",
  className = "",
}: SearchBarProps) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<readonly ProductResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (searchQuery.length < 2) {
      setResults([]);
      setIsOpen(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 5_000);

      const response = await fetch(`${API_URL}/api/catalog/search`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ query: searchQuery, limit: 8 }),
        signal: controller.signal,
      });

      clearTimeout(timeout);

      if (!response.ok) {
        throw new Error("Erreur de recherche");
      }

      const data = await response.json();
      setResults(data.results ?? []);
      setIsOpen(true);
    } catch {
      setError("Recherche indisponible. Parcourez le catalogue ci-dessous.");
      setResults([]);
      setIsOpen(true);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Debounce input changes (300ms)
  useEffect(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [query, performSearch]);

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function handleClear() {
    setQuery("");
    setResults([]);
    setIsOpen(false);
    setError(null);
  }

  function handleSelect(productId: string) {
    setIsOpen(false);
    setQuery("");
    onSelectProduct?.(productId);
  }

  function formatPrice(price: number): string {
    return new Intl.NumberFormat("fr-SN").format(price) + " FCFA";
  }

  return (
    <div ref={containerRef} className={`relative ${className}`}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="pl-10 pr-10 font-sans"
          onFocus={() => {
            if (results.length > 0) setIsOpen(true);
          }}
        />
        {isLoading && (
          <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
        )}
        {!isLoading && query.length > 0 && (
          <button
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground hover:text-foreground"
            aria-label="Effacer la recherche"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && (
        <div className="absolute top-full left-0 right-0 z-50 mt-1 max-h-60 sm:max-h-80 md:max-h-96 overflow-y-auto rounded-lg border bg-background shadow-lg">
          {error && (
            <div className="p-4 text-sm text-muted-foreground font-sans">{error}</div>
          )}

          {results.length === 0 && !error && (
            <div className="p-4 text-sm text-muted-foreground font-sans">
              Aucun résultat pour "{sanitizeDisplayText(query)}"
            </div>
          )}

          {results.map((product) => (
            <button
              key={product.id}
              onClick={() => handleSelect(product.id)}
              className="flex w-full items-center gap-3 p-3 text-left hover:bg-accent transition-colors border-b last:border-b-0"
            >
              {product.image_url && (
                <img
                  src={product.image_url}
                  alt={product.name}
                  className="h-12 w-12 rounded-md object-cover"
                />
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm truncate font-sans">{product.name}</p>
                <p className="text-xs text-muted-foreground font-sans">
                  {product.brand} — {product.category}
                </p>
              </div>
              <span className="text-sm font-semibold text-amber-700 whitespace-nowrap font-sans">
                {formatPrice(product.price_fcfa)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
